import React from 'react';
import PropTypes from 'prop-types';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import classNames from 'classnames';

import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import SectionWrapper from 'components/AddLocation/SectionWrapper';
import CardImage from 'components/App/CardImage';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';

import { ReactComponent as AddPictureIcon } from 'assets/images/add-picture.svg';
import { ReactComponent as NextIconSVG } from 'assets/images/location/next-icon.svg';
import { ReactComponent as PrevIconSVG } from 'assets/images/location/prev-icon.svg';

import { LIMIT_FILE_SIZE } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import TAB from 'constants/tab';
import { editLocationFormSteps } from 'utils/validate';
import formatUploadImages from 'utils/formatUploadImages';

import styles from './StepAddImages.module.scss';

const OPTION_UPLOAD_IMAGES = {
  limitValue: 50,
  fileSize: LIMIT_FILE_SIZE.LARGE,
};

function StepAddImages(props) {
  const {
    isLoadingUploadMultipleMedia,

    actionUploadMultiMedia,
    actionAddLocationByStep,
    onBack,
    setIsValid,
  } = props;

  const [activeId, setActiveId] = React.useState(null);
  const [previewImage, setPreviewImage] = React.useState(null);

  const handlePreviewImage = React.useCallback((imageSrc) => {
    setPreviewImage(imageSrc);
  }, []);

  const handleClosePreview = React.useCallback(() => {
    setPreviewImage(null);
  }, []);

  const inputFileRef = React.useRef(null);

  const validation = useFormik({
    initialValues: {
      images: [],
    },

    validationSchema: Yup.object(editLocationFormSteps.stepAddImages),

    onSubmit: (values) => {
      actionAddLocationByStep({
        location: {
          images: values.images.map((item) => item.image),
        },
        step: TAB.POLICY,
        stepAvailable: TAB.ADD_IMAGES,
      });
    },
  });

  // Drop And Drag
  const sensors = useSensors(
    useSensor(MouseSensor, {
      // Require the mouse to move by 10 pixels before activating
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      // Press delay of 250ms, with tolerance of 5px of movement
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onUploadMultiImages = React.useCallback((imageList) => {
    const imageUploadList = formatUploadImages(imageList, OPTION_UPLOAD_IMAGES);

    if (imageUploadList.length > 0) {
      actionUploadMultiMedia({
        images: imageUploadList,
        callback: (values) => {
          const newValues = values.map((item, index) => ({
            image: item,
            id: index + 1,
          }));

          validation.setFieldValue('images', [...validation.values.images, ...newValues].map((item, index) => ({
            image: item.image,
            id: index + 1,
          })), true);

          inputFileRef.current.value = '';
        },
      });
    }
  }, [inputFileRef, validation.values]);

  const onChangeAddImages = React.useCallback((e) => {
    onUploadMultiImages(e.target.files);
  }, [onUploadMultiImages]);

  const onDragStart = React.useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const onDragCancel = React.useCallback(() => {
    setActiveId(null);
  }, []);

  const onDragImage = React.useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      validation.setValues(({ images }) => {
        const oldIndex = images.findIndex(({ id }) => id === active.id);
        const newIndex = images.findIndex(({ id }) => id === over?.id);
        return ({
          images: arrayMove(images, oldIndex, newIndex),
        });
      });

      setActiveId(null);
    }
  }, [validation.values]);

  // Dropzone
  const {
    getRootProps,
    getInputProps,
    isDragAccept,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: { 'image/*': [] },
    multiple: true,
    onDrop: (files) => onUploadMultiImages(files),
  });

  const onClickOpenFileInput = React.useCallback(() => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  }, [inputFileRef]);

  const onSelectAllImages = React.useCallback(() => {
    validation.setFieldValue('images', validation.values.images.map((item) => ({
      ...item,
      checked: true,
    })), false);
  }, [validation.values]);

  const onClearSelectAllImages = React.useCallback(() => {
    validation.setFieldValue('images', validation.values.images.map((item) => ({
      ...item,
      checked: false,
    })), true);
  }, [validation.values]);

  const onSelectImage = React.useCallback((e) => {
    const { name } = e.target;

    validation.setFieldValue('images', validation.values.images.map((item) => {
      if (item.id === +name) {
        return ({
          ...item,
          checked: !item.checked,
        });
      }

      return { ...item };
    }), true);
  }, [validation.values]);

  const onDeleteSelectedImages = React.useCallback(() => {
    const filterImages = validation.values.images.filter((item) => item.checked !== true);

    validation.setFieldValue('images', filterImages, true);
  }, [validation.values]);

  React.useEffect(() => {
    setIsValid(validation.isValid);
  }, [setIsValid, validation.isValid]);

  return (
    <form className={styles.Component} onSubmit={validation.handleSubmit}>
      <Loading loading={isLoadingUploadMultipleMedia} loaderOnPage />

      <SectionWrapper title="Hình ảnh chỗ nghỉ" className={styles.SectionWrapper}>
        {validation.values.images.length > 0 && (
          <div className={styles.ButtonWrap}>
            <Button
              isDefault
              classes={styles.ButtonCheckAll}
              onClick={onSelectAllImages}
            >
              Chọn tất cả
            </Button>

            <Button
              isDefault
              classes={styles.ButtonUnCheck}
              onClick={onClearSelectAllImages}
              disabled={!validation.values.images.some((item) => item.checked)}
            >
              Bỏ chọn tất cả
            </Button>

            <Button
              isDefault
              classes={styles.ButtonRemove}
              onClick={onDeleteSelectedImages}
              disabled={!validation.values.images.some((item) => item.checked)}
            >
              Xóa
            </Button>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragImage}
          onDragStart={onDragStart}
          onDragCancel={onDragCancel}
        >
          <SortableContext
            items={validation.values.images.map((item) => item.id)}
            strategy={rectSortingStrategy}
          >
            <div className={styles.ImageList} {...getRootProps()}>
              <div className={classNames(
                styles.DropImageLayout,
                isDragAccept && styles.DropImageActive,
              )}
              >
                <AddPictureIcon width={128} height={128} />

                <span>THÊM ẢNH</span>
              </div>

              <input
                {...getInputProps()}
                ref={inputFileRef}
                onChange={onChangeAddImages}
              />

              {validation.values.images.map((image) => (
                <CardImage
                  key={image.id}
                  item={image}
                  onSelectImage={onSelectImage}
                  onPreviewImage={handlePreviewImage}
                  className={styles.CardImageItem}
                />
              ))}

              <Button
                isDefault
                id="file"
                type="button"
                classes={classNames(
                  styles.UploadImage,
                  validation.values.images.length === 0 && styles.UploadImageFull,
                )}
                onClick={onClickOpenFileInput}
              >

                <AddPictureIcon />

                <span>THÊM ẢNH</span>
              </Button>
            </div>
          </SortableContext>

          <DragOverlay adjustScale style={{ transformOrigin: '0 0 ' }}>
            {activeId ? (
              <CardImage
                item={activeId}
                isDraggingProps
                className={styles.CardImageItem}
              />
            ) : null}
          </DragOverlay>
        </DndContext>

        {validation.errors.images && (
          <p className={styles.Error}>{validation.errors.images}</p>
        )}

        <span className={styles.DescriptionTitle}>Tải ít nhất 4 hình lên</span>

        <section className={styles.DescriptionContent}>
          <span>
            Hình ảnh đẹp giống như lời mời gọi hấp dẫn về một trải nghiệm trọn vẹn tại chỗ nghỉ,
            hãy đăng tải những hình có độ phân giải cao
            và thể hiện chính xác những gì chỗ nghỉ Quý vị có.
            Chúng tôi sẽ hiển thị những hình này trong trang chỗ nghỉ của Quý vị
            trên trang web Bookese.com.
          </span>

          <span>
            Quý vị có thể sử dụng: Điện thoại thông minh hoặc Máy ảnh kỹ thuật số để chụp ảnh.
          </span>

          <span>
            Nếu không biết ai là chủ sở hữu của bức ảnh,
            tốt nhất Quý vị không nên sử dụng.
            Quý vị chỉ nên sử dụng ảnh mình có bản quyền hoặc của một người khác
            nhưng đã có sự chấp thuận để sử dụng.
          </span>
        </section>
      </SectionWrapper>

      <div className={styles.FormAction}>
        <Button
          classes="Btn Btn__PrevStep"
          outline
          onClick={onBack(TAB.ROOM_PRICE)}
        >
          <PrevIconSVG className="Icon__PrevStep" />
          Quay về bước trước
        </Button>

        <Button
          classes="Btn Btn__NextStep"
          type="submit"
          disabled={validation.errors.images}
        >
          Bước tiếp theo
          <NextIconSVG className="Icon__NextStep" />
        </Button>
      </div>

      <Modal isOpen={!!previewImage} toggle={handleClosePreview} size="lg" centered>
        <ModalHeader toggle={handleClosePreview} className="border-0 pb-0" />
        <ModalBody className="text-center p-3 overflow-hidden d-flex justify-content-center align-items-center">
          <img
            src={previewImage}
            alt="Preview"
            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: '4px' }}
          />
        </ModalBody>
      </Modal>
    </form>
  );
}

StepAddImages.propTypes = {
  isLoadingUploadMultipleMedia: PropTypes.bool,

  actionUploadMultiMedia: PropTypes.func,
  actionAddLocationByStep: PropTypes.func,
  onBack: PropTypes.func,
  setIsValid: PropTypes.func,
};

StepAddImages.defaultProps = {
  isLoadingUploadMultipleMedia: false,

  actionUploadMultiMedia: INITIAL_VALUE.FUNCTION,
  actionAddLocationByStep: INITIAL_VALUE.FUNCTION,
  onBack: INITIAL_VALUE.FUNCTION,
  setIsValid: INITIAL_VALUE.FUNCTION,
};

export default React.memo(StepAddImages);
